/**
 * Utilities for inspecting RTK Query / fetch errors.
 *
 * @module errorUtilities
 */

/**
 * HTTP status returned by the backend when an optimistic-concurrency check
 * fails — i.e. the row the user is editing was changed by another process
 * since it was loaded (stale row version).
 *
 * @type {number}
 */
export const CONFLICT_STATUS = 409

/**
 * Determines whether an error is an HTTP 409 Conflict.
 *
 * RTK Query's `fetchBaseQuery` rejects `.unwrap()` with `{ status, data }`,
 * where `status` is the HTTP status code for server responses. Use this to
 * branch a `catch` block onto a "refresh and try again" message instead of a
 * generic error message.
 *
 * @param {{ status?: number | string }} [error] - The error thrown by `.unwrap()`.
 * @returns {boolean} `true` when the error represents an HTTP 409 Conflict.
 */
export const isConflictError = (error) => error?.status === CONFLICT_STATUS

/**
 * Extracts a human-readable message from an RTK Query error's response body.
 *
 * Several admin endpoints (suspend/unsuspend user or organisation, set user role) return
 * their failure body in one of two shapes: a bare JSON string (e.g. a self-suspend guard),
 * or `{ success: false, error: string }`. Both are handled here so callers don't each
 * reimplement the same `typeof` check.
 *
 * @param {{ data?: string | { error?: string } }} [error] - The error thrown by `.unwrap()`.
 * @returns {string | undefined} The extracted message, or `undefined` if the body has neither shape.
 */
export const getErrorMessage = (error) => (typeof error?.data === 'string' ? error.data : error?.data?.error)
